import { theme as antdTheme } from 'antd';

import type { ThemeConfig } from 'antd';

const BRAND = {
  orange: '#FA7616',
  orangeHover: '#FF8A36',
  orangeActive: '#E56200',
  orangeSoft: '#FFF2EA',
  dark: '#1E1E1E',
  darkSoft: '#2A2A2A',
  darkElevated: '#333333',
  lightLayout: '#F5F6FA',
  white: '#FFFFFF',
  gold: '#D6A84B',
  goldSoft: '#F4D98B',
  holoPurple: '#8B5CF6',
  holoMagenta: '#D946EF',
  slate950: '#1E1E1E',
  slate900: '#262626',
  slate800: '#383838',
  slate700: '#525252',
  slate600: '#737373',
  slate500: '#8C8C8C',
  slate400: '#A3A3A3',
  slate300: '#D4D4D4',
  slate200: '#E5E5E5',
  slate100: '#F0F0F0',
  slate50: '#F5F5F5',
  success: '#22C55E',
  warning: '#F59E0B',
  error: '#D65C55',
  info: '#4388C7',
  infoBg: '#EFF6FF',
  infoText: '#285A86',
} as const;

export const APP_THEME_MODE = { DARK: 'dark', LIGHT: 'light' } as const;

export type AppThemeMode = (typeof APP_THEME_MODE)[keyof typeof APP_THEME_MODE];

export const THEME_STORAGE_KEY = 'tcg-marketplace-theme';

export const nexoSpacing = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  24: 96,
} as const;

export const nexoSemanticSpacing = {
  xs: nexoSpacing[1],
  sm: nexoSpacing[2],
  md: nexoSpacing[4],
  lg: nexoSpacing[6],
  xl: nexoSpacing[8],
  xxl: nexoSpacing[12],
  section: nexoSpacing[16],
} as const;

export const nexoRadius = {
  none: 0,
  xs: 4,
  sm: 6,
  md: 10,
  lg: 14,
  xl: 18,
  xxl: 24,
  pill: 999,
} as const;

export const nexoTypography = {
  display: {
    fontSize: 48,
    lineHeight: 1.08,
    fontWeight: 700,
    letterSpacing: '-0.03em',
  },
  h1: {
    fontSize: 36,
    lineHeight: 1.15,
    fontWeight: 700,
    letterSpacing: '-0.025em',
  },
  h2: {
    fontSize: 30,
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },
  h3: {
    fontSize: 24,
    lineHeight: 1.25,
    fontWeight: 600,
    letterSpacing: '-0.015em',
  },
  h4: { fontSize: 20, lineHeight: 1.3, fontWeight: 600 },
  h5: { fontSize: 16, lineHeight: 1.4, fontWeight: 600 },
  h6: {
    fontSize: 14,
    lineHeight: 1.4,
    fontWeight: 600,
    letterSpacing: '0.01em',
  },
  body: { fontSize: 14, lineHeight: 1.6, fontWeight: 400 },
  bodyLarge: { fontSize: 16, lineHeight: 1.6, fontWeight: 400 },
  caption: { fontSize: 12, lineHeight: 1.5, fontWeight: 400 },
} as const;

export const nexoShadows = {
  light: {
    xs: '0 1px 2px rgba(30, 30, 30, 0.05)',
    sm: '0 1px 3px rgba(30, 30, 30, 0.08), 0 1px 2px rgba(30, 30, 30, 0.04)',
    md: '0 4px 12px rgba(30, 30, 30, 0.08), 0 2px 4px rgba(30, 30, 30, 0.04)',
    lg: '0 10px 28px rgba(30, 30, 30, 0.10), 0 4px 10px rgba(30, 30, 30, 0.05)',
    xl: '0 20px 48px rgba(30, 30, 30, 0.12), 0 8px 20px rgba(30, 30, 30, 0.06)',
    focus: '0 0 0 3px rgba(250, 118, 22, 0.18)',
    energy:
      '0 0 0 1px rgba(250, 118, 22, 0.22), 0 8px 24px rgba(250, 118, 22, 0.16)',
    premium:
      '0 0 0 1px rgba(214, 168, 75, 0.24), 0 10px 28px rgba(214, 168, 75, 0.10)',
  },
  dark: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.30)',
    sm: '0 2px 6px rgba(0, 0, 0, 0.32), 0 1px 2px rgba(0, 0, 0, 0.24)',
    md: '0 6px 18px rgba(0, 0, 0, 0.34), 0 2px 6px rgba(0, 0, 0, 0.24)',
    lg: '0 12px 32px rgba(0, 0, 0, 0.40), 0 4px 12px rgba(0, 0, 0, 0.28)',
    xl: '0 24px 64px rgba(0, 0, 0, 0.48), 0 10px 24px rgba(0, 0, 0, 0.32)',
    focus: '0 0 0 3px rgba(250, 118, 22, 0.22)',
    energy:
      '0 0 0 1px rgba(250, 118, 22, 0.30), 0 0 22px rgba(250, 118, 22, 0.18), 0 10px 30px rgba(0, 0, 0, 0.40)',
    premium:
      '0 0 0 1px rgba(214, 168, 75, 0.24), 0 0 20px rgba(214, 168, 75, 0.08)',
  },
} as const;

export const nexoGradients = {
  brand: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeHover} 100%)`,
  energy: `linear-gradient(135deg, ${BRAND.orangeActive} 0%, ${BRAND.orange} 50%, ${BRAND.orangeHover} 100%)`,
  holographic: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.gold} 50%, ${BRAND.holoPurple} 100%)`,
  premium: `linear-gradient(135deg, ${BRAND.gold} 0%, ${BRAND.goldSoft} 100%)`,
} as const;

const commonTokens = {
  borderRadiusXS: nexoRadius.xs,
  borderRadiusSM: nexoRadius.sm,
  borderRadius: nexoRadius.md,
  borderRadiusLG: nexoRadius.lg,
  paddingXXS: nexoSpacing[1],
  paddingXS: nexoSpacing[2],
  paddingSM: nexoSpacing[3],
  padding: nexoSpacing[4],
  paddingMD: nexoSpacing[5],
  paddingLG: nexoSpacing[6],
  paddingXL: nexoSpacing[8],
  marginXXS: nexoSpacing[1],
  marginXS: nexoSpacing[2],
  marginSM: nexoSpacing[3],
  margin: nexoSpacing[4],
  marginMD: nexoSpacing[5],
  marginLG: nexoSpacing[6],
  marginXL: nexoSpacing[8],
  marginXXL: nexoSpacing[12],
  controlHeightSM: 32,
  controlHeight: 40,
  controlHeightLG: 48,
  fontFamily: '"Inter", sans-serif',
  fontFamilyCode: '"JetBrains Mono", "Fira Code", monospace',
  fontSize: nexoTypography.body.fontSize,
  fontSizeSM: nexoTypography.caption.fontSize,
  fontSizeLG: nexoTypography.bodyLarge.fontSize,
  fontSizeHeading1: nexoTypography.h1.fontSize,
  fontSizeHeading2: nexoTypography.h2.fontSize,
  fontSizeHeading3: nexoTypography.h3.fontSize,
  fontSizeHeading4: nexoTypography.h4.fontSize,
  fontSizeHeading5: nexoTypography.h5.fontSize,
  lineHeightHeading1: nexoTypography.h1.lineHeight,
  lineHeightHeading2: nexoTypography.h2.lineHeight,
  lineHeightHeading3: nexoTypography.h3.lineHeight,
  lineHeightHeading4: nexoTypography.h4.lineHeight,
  lineHeightHeading5: nexoTypography.h5.lineHeight,
  fontWeightStrong: 600,
} as const;

export const nexoLightTheme: ThemeConfig = {
  algorithm: antdTheme.defaultAlgorithm,
  cssVar: {},
  token: {
    ...commonTokens,
    colorPrimary: BRAND.orange,
    colorInfo: BRAND.info,
    colorInfoBg: BRAND.infoBg,
    colorInfoText: BRAND.infoText,
    colorSuccess: BRAND.success,
    colorWarning: BRAND.warning,
    colorError: BRAND.error,
    colorBgBase: BRAND.white,
    colorBgLayout: BRAND.lightLayout,
    colorBgContainer: BRAND.white,
    colorBgElevated: BRAND.white,
    colorText: BRAND.slate950,
    colorTextSecondary: BRAND.slate700,
    colorTextDescription: BRAND.slate700,
    colorTextPlaceholder: BRAND.slate700,
    colorTextTertiary: BRAND.slate700,
    colorTextQuaternary: BRAND.slate400,
    colorBorder: BRAND.slate200,
    colorBorderSecondary: BRAND.slate100,
    colorLink: '#A34300',
    colorLinkHover: '#863700',
    colorLinkActive: BRAND.orangeActive,
    boxShadow: nexoShadows.light.lg,
    boxShadowSecondary: nexoShadows.light.md,
    boxShadowTertiary: nexoShadows.light.sm,
  },
  components: {
    Form: {
      itemMarginBottom: nexoSpacing[3],
      verticalLabelPadding: `0 0 ${nexoSpacing[1]}px`,
    },
    Layout: {
      bodyBg: BRAND.lightLayout,
      headerBg: BRAND.white,
      siderBg: BRAND.white,
      headerPadding: 0,
    },
    Card: {
      borderRadiusLG: nexoRadius.lg,
      colorBorderSecondary: BRAND.slate200,
      boxShadowTertiary: nexoShadows.light.sm,
      paddingLG: nexoSpacing[6],
    },
    Button: {
      primaryColor: BRAND.dark,
      borderRadius: nexoRadius.md,
      borderRadiusLG: nexoRadius.lg,
      primaryShadow: '0 4px 14px rgba(250, 118, 22, 0.28)',
      defaultBorderColor: BRAND.slate200,
      defaultColor: BRAND.slate800,
      defaultHoverBorderColor: BRAND.orange,
      defaultHoverColor: BRAND.orange,
    },
    Input: {
      borderRadius: nexoRadius.md,
      activeBorderColor: BRAND.orange,
      hoverBorderColor: BRAND.orange,
      activeShadow: nexoShadows.light.focus,
    },
    Select: {
      borderRadius: nexoRadius.md,
      optionSelectedBg: BRAND.orangeSoft,
      optionSelectedColor: BRAND.orangeActive,
    },
    Modal: {
      borderRadiusLG: nexoRadius.xl,
      paddingContentHorizontalLG: nexoSpacing[6],
      paddingMD: nexoSpacing[5],
    },
    Drawer: { paddingLG: nexoSpacing[6] },
    Table: {
      headerBg: BRAND.lightLayout,
      headerColor: BRAND.slate800,
      rowHoverBg: BRAND.orangeSoft,
      borderColor: BRAND.slate200,
      cellPaddingBlock: nexoSpacing[3],
      cellPaddingInline: nexoSpacing[4],
    },
    Tabs: {
      itemColor: BRAND.slate500,
      itemHoverColor: BRAND.orange,
      itemSelectedColor: BRAND.orange,
      inkBarColor: BRAND.orange,
      horizontalItemGutter: nexoSpacing[8],
    },
    Tooltip: {
      borderRadius: nexoRadius.sm,
      colorBgSpotlight: BRAND.dark,
      colorTextLightSolid: BRAND.white,
    },
    Tag: {
      borderRadiusSM: nexoRadius.pill,
      defaultBg: BRAND.slate100,
      defaultColor: BRAND.slate700,
    },
    Segmented: { borderRadius: nexoRadius.md, itemSelectedBg: BRAND.white },
  },
};

export const nexoDarkTheme: ThemeConfig = {
  algorithm: antdTheme.darkAlgorithm,
  cssVar: {},
  token: {
    ...commonTokens,
    colorPrimary: BRAND.orange,
    colorInfo: BRAND.orange,
    colorSuccess: BRAND.success,
    colorWarning: BRAND.warning,
    colorError: BRAND.error,
    colorBgBase: BRAND.dark,
    colorBgLayout: '#141414',
    colorBgContainer: BRAND.darkSoft,
    colorBgElevated: BRAND.darkElevated,
    colorText: BRAND.lightLayout,
    colorTextSecondary: BRAND.slate300,
    colorTextTertiary: BRAND.slate400,
    colorTextQuaternary: BRAND.slate500,
    colorBorder: '#383838',
    colorBorderSecondary: '#2B2B2B',
    colorLink: BRAND.orange,
    colorLinkHover: BRAND.orangeHover,
    colorLinkActive: BRAND.orangeActive,
    boxShadow: nexoShadows.dark.lg,
    boxShadowSecondary: nexoShadows.dark.md,
    boxShadowTertiary: nexoShadows.dark.sm,
  },
  components: {
    Layout: {
      bodyBg: '#141414',
      headerBg: BRAND.darkSoft,
      siderBg: BRAND.darkSoft,
      headerPadding: 0,
    },
    Card: {
      borderRadiusLG: nexoRadius.lg,
      colorBorderSecondary: '#333333',
      boxShadowTertiary: nexoShadows.dark.sm,
      paddingLG: nexoSpacing[6],
    },
    Button: {
      primaryColor: BRAND.dark,
      borderRadius: nexoRadius.md,
      borderRadiusLG: nexoRadius.lg,
      primaryShadow: nexoShadows.dark.energy,
      defaultBg: BRAND.darkElevated,
      defaultBorderColor: '#424242',
      defaultColor: BRAND.lightLayout,
      defaultHoverBorderColor: BRAND.orange,
      defaultHoverColor: BRAND.orange,
    },
    Input: {
      borderRadius: nexoRadius.md,
      colorBgContainer: BRAND.darkElevated,
      activeBorderColor: BRAND.orange,
      hoverBorderColor: BRAND.orangeHover,
      activeShadow: nexoShadows.dark.focus,
    },
    Select: {
      borderRadius: nexoRadius.md,
      colorBgContainer: BRAND.darkElevated,
      optionSelectedBg: 'rgba(250, 118, 22, 0.22)',
      optionSelectedColor: BRAND.orange,
    },
    Modal: {
      borderRadiusLG: nexoRadius.xl,
      paddingContentHorizontalLG: nexoSpacing[6],
      paddingMD: nexoSpacing[5],
    },
    Drawer: { paddingLG: nexoSpacing[6] },
    Table: {
      headerBg: BRAND.darkElevated,
      headerColor: BRAND.lightLayout,
      rowHoverBg: 'rgba(250, 118, 22, 0.12)',
      borderColor: '#333333',
      cellPaddingBlock: nexoSpacing[3],
      cellPaddingInline: nexoSpacing[4],
    },
    Tabs: {
      itemColor: BRAND.slate400,
      itemHoverColor: BRAND.orangeHover,
      itemSelectedColor: BRAND.lightLayout,
      inkBarColor: BRAND.orange,
      horizontalItemGutter: nexoSpacing[8],
    },
    Tooltip: {
      borderRadius: nexoRadius.sm,
      colorBgSpotlight: '#0A0A0A',
      colorTextLightSolid: BRAND.lightLayout,
    },
    Tag: {
      borderRadiusSM: nexoRadius.pill,
      defaultBg: '#2A2A2A',
      defaultColor: BRAND.slate300,
    },
    Segmented: {
      borderRadius: nexoRadius.md,
      itemSelectedBg: BRAND.darkElevated,
    },
  },
};

export const nexoBrandColors = BRAND;

export const THEME_COLORS = {
  PRIMARY: BRAND.orange,
  PRIMARY_DARK: BRAND.orangeActive,
  PRIMARY_SOFT: BRAND.orangeSoft,
  SECONDARY: BRAND.dark,
  PREMIUM: BRAND.gold,
  BRONZE: BRAND.gold,
  DEEP_BLUE: BRAND.dark,
  GOLD: BRAND.gold,
  GOLD_HOVER: BRAND.goldSoft,
  GOLD_SOFT: BRAND.goldSoft,
  METAL: BRAND.slate300,
  DEEP_BLACK: BRAND.dark,
  NIGHT_BROWN: BRAND.slate950,
  WARM_BROWN: BRAND.slate800,
  CREAM: BRAND.lightLayout,
  SUCCESS: BRAND.success,
  WARNING: BRAND.warning,
  ERROR: BRAND.error,
  LIGHT: { ELEVATED_SURFACE: BRAND.white },
  DARK: { ELEVATED_SURFACE: BRAND.darkElevated },
} as const;

export const createAppTheme = (mode: AppThemeMode): ThemeConfig =>
  mode === APP_THEME_MODE.DARK ? nexoDarkTheme : nexoLightTheme;
